/**
 * This file contains many functions that make calls to our API and return the promises from those calls.
 */
import { parallelLimit } from 'async';
import { chain, chunk, flatMap, range } from 'lodash';
import queryString from 'querystring';
import qs from 'qs';

import Api from '../api';
import uuidToDate from '../Utils/Date/uuid-to-date';
import { TOKEN_STORAGE_KEY } from '../../Containers/App/reducer';
import { PrintingOnboarding, PrintingPermissionRaw } from './asn';
import { RolesType } from '../Constants/roles';

const VENDOR = '/vendors';
const ONBOARDING_VENDOR = '/onboarding-vendor';

export interface Vendor {
  vendor_type: string;
  netsuite_status: string;
  id: string;
  email: string;
  sponsor_id: number;
  custom_payment_terms: string | null;
  application_status: string;
  company_name: string;
  legal_name: string;
  first_name?: string | null;
  last_name?: string | null;
  is_individual: boolean;
  internal_id: string;
  influencer_handles: Handle[];
  influencer_handle: string;
  influencer_platform: string;
  country: string;
  contacts: Contact[];
  zip: string;
  existing_vendor: string;
  merch_type: string;
  contact: string;
  application_archived_at: string;
  inviteTokens: InviteToken[];
  updated_at: string;
  domestic_merch_po_eligible?: boolean;
  printing_onboarding?: PrintingOnboarding;
  printing_permissions?: PrintingPermissionRaw[];
  inactive?: boolean;
  coi_expiration_date?: string | null;
  // TODO: [HALO-MIGRATION] Remove migrated_to_halo field after migration is complete
  migrated_to_halo?: string | null;
}

export interface Contact {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  phone: string;
  phone_country: string;
  title: string;
  role: string;
  vendor_id: string;
  vendor_name: null | string;
  deleted_at: string | null;
  registered_at: string | null;
  last_logged_in_at: string | null;
  updated_at: string;
}

export interface Handle {
  social_media_handle: string;
  social_media_platform: string;
  primary: boolean;
}

export type UserFeatureAccess = Record<string, 'enabled' | 'disabled'>;

export interface User {
  email: string;
  first_name: string;
  id: number;
  last_logged_in_at: string;
  last_name: string;
  phone: string | null;
  phone_country: string;
  registered_at: string;
  role: RolesType;
  title: string | null;
  vendor?: Vendor | null;
  vendor_id: string;
  vendor_name: string;
  feature_permissions: {
    allow_printing?: boolean;
    internal_print?: boolean;
  };
  feature_flag_access: UserFeatureAccess;
  // TODO: [HALO-MIGRATION] Remove migrated_to_halo field after migration is complete
  migrated_to_halo?: string | null;
}

export interface InviteToken {
  expires_at: string;
  id: number;
  status: string;
  token: string;
  updatedAt: string;
}

export interface Application {
  current_step: string;
  status: string;
  vendor: Vendor;
  updated_at: string;
}

export interface FormattedApplication {
  application_stage: string;
  contact: string;
  last_updated_at: string;
  stage_status: string;
  vendor_application_archived_at: string | null;
  vendor_application_status: string;
  inviteTokens: InviteToken[];
  vendor_id: string;
  vendor_name: string;
  vendor_type: string;
}

interface PaymentData {
  id: string;
}

export interface InvoiceItem {
  amount: number | null;
  bill_internal_id: string;
  bill_line_id: string;
  color: string | null;
  deleted_at: string | null;
  description: string | null;
  id: string;
  item: string | null;
  line_id: number | null;
  quantity: number | null;
  rate: number | null;
  size: string | null;
  updated_at: string | null;
  vendor_style: string | null;
}
export interface BillCreditLineItem {
  id: string;
  credit_internal_id: string;
  line_id: string;
  credit_line_id: string;
  item: string | null;
  description: string | null;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface BillCredit {
  id: string;
  internalId: number;
  date: string | null;
  reference_number: string;
  memo: string;
  amount: number;
  vendor_internal_id: number;
  items: BillCreditLineItem[];
}

export interface Invoice {
  discount_amount: number | null;
  discount_date: string | null;
  due_date: string;
  id: string;
  internal_id: string;
  invoice_number: string;
  items: InvoiceItem[];
  payments: PaymentData[];
  po_internal_id: string;
  po_number: string;
  status: string;
  terms: string;
  total: number;
  vendor_internal_id: string;
}
interface VendorFilter {
  printing_onboarding?: {
    value?: null;
  };
  printed_samples?: {
    value?: {
      unit: string;
      carton: string;
      hangtag: string;
    };
    any?: boolean;
  };
  printing_approved_at?: {
    value?: null;
    ne?: boolean;
  };
  pending_approval_reason?: {
    value?: null;
    ne?: boolean;
  };
}

const VendorAPI = {
  /**
   *
   * @param {string} params - query params
   *
   * @returns {Promise} response:
   * example: {
   *   vendors: [{
   *     ...vendor
   *   }]
   * }
   */
  async getVendors(params = '') {
    return Api.get(`${VENDOR}?${params}`);
  },

  /**
   * Get vendors for export
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of bill payments.
   *
   * example: vendors: [billPayment1, billPayment2, ...]
   */
  async getVendorsExport() {
    const limit = 100;
    const firstPage = await Api.get(
      `${VENDOR}?${queryString.encode({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const vendors = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return Api.get(`${VENDOR}?${params}`);
      }),
    );

    return flatMap(vendors, 'rows');
  },

  async updateVendorGrades(grades: unknown[]) {
    const tasks = chunk(grades, 20).map(
      (gradeChunk) => (cb: (error: Error | null, result?: unknown) => void) =>
        Api.put(`${VENDOR}/grades`, { data: { grades: gradeChunk } })
          .then((response) => cb(null, response))
          .catch(cb),
    );

    return parallelLimit(tasks, 5);
  },

  /**
   * Makes call to api endpoint to grab deliverable purchase orders
   *
   * @param id
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrders: [POobject1, POobject2, ...]
   * }
   */
  getDeliverablePurchaseOrders(id: string, location_id: string, params: any) {
    return Api.get(
      `${VENDOR}/${id}/purchase-orders/deliverable?location_id=${location_id}&${queryString.encode(
        params,
      )}`,
    );
  },

  /**
   * Makes call to api endpoint to grab the compliance report
   *
   * @param from
   * @param to
   *
   * @returns {promise} promise returned from the api call
   */
  async generateComplianceReport(vendorFilters?: VendorFilter) {
    const limit = 15;

    const queryParams = qs.stringify({
      vendorFilters,
      limit,
    });

    const firstPage = await Api.get(
      `${VENDOR}/reports/compliance?${queryParams}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const vendors = await parallelLimit(
      range(totalPages).map((page) => {
        const pageParams = qs.stringify({
          vendorFilters,
          limit,
          offset: page * limit,
        });

        return (cb: (c1: null, c2: void) => void) =>
          Api.get(`${VENDOR}/reports/compliance?${pageParams}`)
            .then((v) => cb(null, v.rows))
            .catch(cb);
      }),
      5,
    ).catch(console.log);

    const uniqueVendors = chain<Vendor>(vendors as Vendor)
      .flatMap()
      .uniqBy('id')
      .map((v: Vendor) => ({ ...v, date_added: uuidToDate(v.id) }))
      .sortBy('date_added')
      .reverse()
      .value();

    return uniqueVendors;
  },

  initVendorApplication(formData: InitVendorApplicationData) {
    return Api.post(`${VENDOR}/init-application`, { data: formData });
  },

  getApplication(id: string) {
    return Api.get(`${ONBOARDING_VENDOR}/${id}`);
  },

  getVendorOnboardingSignedUrls(id: string, fileName: string) {
    return Api.get(`${ONBOARDING_VENDOR}/${id}/file/${fileName}`);
  },

  getApplications(params: string) {
    return Api.get(`${ONBOARDING_VENDOR}?${params}`);
  },

  refreshTokenVendorApplication(refreshTokenId: string) {
    return Api.post(`${VENDOR}/init-application/${refreshTokenId}/refresh`);
  },

  updateVendorInfo({ data, id }: UpdateVendorInfoData) {
    return Api.post(`${ONBOARDING_VENDOR}/info`, {
      data: { data, id },
    });
  },

  saveApplicationWithoutValidation({
    field,
    value,
    step,
    id,
  }: SaveApplicationWithoutValidationData) {
    return Api.post(`${ONBOARDING_VENDOR}/save`, {
      data: { id, field, value, step },
    });
  },

  validateVendorStep({ answers, step, id }: ValidateVendorStepData) {
    return Api.post(`${ONBOARDING_VENDOR}/vendor_step_validation`, {
      data: { answers, step, id },
    });
  },

  validateBuyerStep({ answers, step, id }: ValidateBuyerStepData) {
    return Api.post(`${ONBOARDING_VENDOR}/buyer_step_validation`, {
      data: { answers, step, id },
    });
  },

  submitVendorApplication({ id }: SubmitVendorApplicationData) {
    return Api.post(`${ONBOARDING_VENDOR}/submit_vendor_questionnaire`, {
      data: { id },
    });
  },

  submitBuyerApplication({ id }: SubmitBuyerApplicationData) {
    return Api.post(`${ONBOARDING_VENDOR}/submit_buyer_questionnaire`, {
      data: { id },
    });
  },

  sponsorApproval({ id, approved }: SponsorApprovalData) {
    return Api.post(`${ONBOARDING_VENDOR}/sponsor_approval`, {
      data: { id, approved },
    });
  },

  changeSponsorApproval({ id }: ChangeSponsorApprovalData) {
    return Api.post(`${ONBOARDING_VENDOR}/change_sponsor_approval`, {
      data: { id },
    });
  },

  firstExecutiveApproval({ id, approved }: FirstExecutiveApprovalData) {
    return Api.post(`${ONBOARDING_VENDOR}/first_executive_approval`, {
      data: { id, approved },
    });
  },

  secondExecutiveApproval({ id, approved }: SecondExecutiveApprovalData) {
    return Api.post(`${ONBOARDING_VENDOR}/second_executive_approval`, {
      data: { id, approved },
    });
  },

  archiveApplication({ id }: ArchiveApplicationData) {
    return Api.post(`${ONBOARDING_VENDOR}/archive`, {
      data: { id },
    });
  },

  unArchiveApplication({ id }: UnArchiveApplicationData) {
    return Api.post(`${ONBOARDING_VENDOR}/unarchive`, {
      data: { id },
    });
  },

  submitContactForm(data: ContactFormData) {
    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        user: data.user,
        flow: data.flow,
        description: data.description,
      }),
    );
    data.files.forEach((file) => {
      formData.append(`files`, file);
    });
    return Api.post('/vendors/contact', {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Auth-Token': localStorage.getItem(TOKEN_STORAGE_KEY),
      },
    });
  },
};

export interface UnArchiveApplicationData {
  id: string;
}

export interface ArchiveApplicationData {
  id: string;
}

export interface InitVendorApplicationData {
  formData: unknown;
}

export interface UpdateVendorInfoData {
  data: unknown;
  id: string;
}

export interface SaveApplicationWithoutValidationData {
  field: string;
  value: unknown;
  step: string;
  id: string;
}

export interface ValidateVendorStepData {
  answers: unknown;
  step: string;
  id: string;
}

export interface ValidateBuyerStepData {
  answers: unknown;
  step: string;
  id: string;
}

export interface SubmitVendorApplicationData {
  id: string;
}

export interface SubmitBuyerApplicationData {
  id: string;
}

export interface SponsorApprovalData {
  id: string;
  approved: boolean;
}

export interface ChangeSponsorApprovalData {
  id: string;
}

export interface FirstExecutiveApprovalData {
  id: string;
  approved: boolean;
}

export interface SecondExecutiveApprovalData {
  id: string;
  approved: boolean;
}

export interface ContactFormData {
  user: {
    email: string;
    name?: string;
    company_name?: string;
    first_name?: string;
    last_name?: string;
  };
  flow: string[];
  description: string;
  files: File[];
}

export default VendorAPI;
